import {
    FC,
    ReactElement,
    useEffect,
    useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { PacoLogsModal } from '../../../../@paco/compositions'
import { AbsenceFormData } from '../../../../@paco/entities/Absence/Absence'
import { transformLegacyAbsenceToAbsence } from '../../../../@paco/entities/Absence/AbsenceTransformers'
import {
    transformLegacyLeaveOfAbsenceToLeaveOfAbsence,
} from '../../../../@paco/entities/LeaveOfAbsence/LeaveOfAbsenceTransformers'
import {
    addDays,
    addSeconds,
    getEndOfWorkDay,
    getStartOfWorkDay,
} from '../../../../@paco/helpers/date'
import checkPermission from '../../../../@paco/helpers/permissions/checkPermission'
import { setUserSearch } from '../../../../@paco/redux/@interface/globalFilters/globalFiltersReducer'
import { deleteAbsence, patchAbsence } from '../../../../@paco/redux/absences/absencesActions'
import { setError as setAbsencesError } from '../../../../@paco/redux/absences/absencesReducer'
import { getEventLogsByEntityUuid } from '../../../../@paco/redux/eventLogs/eventLogsActions'
import {
    approveLeaveOfAbsence,
    declineLeaveOfAbsence,
    deleteLeaveOfAbsence,
    patchLeaveOfAbsence,
} from '../../../../@paco/redux/leaveOfAbsences/leaveOfAbsencesActions'
import { setError as setLeaveOfAbsencesError } from '../../../../@paco/redux/leaveOfAbsences/leaveOfAbsencesReducer'
import { getPacoPayrollPeriods } from '../../../../@paco/redux/payrollPeriods/payrollPeriodsActions'
import {
    setIsDeleteSuccessful as setIsShiftConceptPlanningDeleteSuccessful,
    setIsEditSuccessful as setIsShiftConceptPlanningSuccessful,
} from '../../../../@paco/redux/shiftConceptPlanning/shiftConceptPlanningReducer'
import { fetchSpecialDays } from '../../../../@paco/redux/specialDays/specialDaysActions'
import { useTypedSelector } from '../../../../@paco/redux/store'
import { setToast } from '../../../../@paco/redux/toasts/toastsReducer'
import { TimeModeType, ToastType } from '../../../../@paco/types'
import { PzLeaveType } from '../../../../@paco/types/leaveType'

import NewFilters from '../../../../components/Filters/Filters'
import SidebarPage from '../../../../components/SidebarPage/SidebarPage'
import { CONCEPTS_LIST_VIEW } from '../../../../constants'
import { PublishBaseScheduleFormData, ShiftConceptViewModelOld } from '../../../../entities/ShiftConcept/ShiftConcept'
import { translate } from '../../../../helpers/translations/translator'
import {
    Absence,
    LeaveOfAbsence,
    LeaveOfAbsenceEditType,
    LeaveOfAbsenceFormData,
    LeaveType,
    PayrollPeriodViewModel,
    PayrollPeriodWithHours,
    WeekWithHours,
} from '../../../../models'
import removeShiftConceptPlanningFromUsersWithConceptAvailability
    from '../../../../redux/@toolkit/shiftConcepts/helpers/removeShiftPlanningFromUsersWithConceptAvailability'
import {
    updateUsersWithConceptAvailabilityWithShiftPlanning,
} from '../../../../redux/@toolkit/shiftConcepts/helpers/updateUsersWithConceptAvailabilityWithShiftPlanning'
import {
    fetchShiftConceptPlannings,
    getUsersListViewData,
    planAvailabilityUserToShiftConcept,
    publishBaseSchedule,
} from '../../../../redux/@toolkit/shiftConcepts/shiftConceptsActions'
import {
    setAvailabilityPagination,
    setUserWithConceptAvailability,
} from '../../../../redux/@toolkit/shiftConcepts/shiftConceptsReducer'
import { getPayrollPeriods, switchMode } from '../../../../redux/app/appActions'
import { setFilter } from '../../../../redux/filter/filterActions'
import { AppFilter } from '../../../../redux/filter-ts/filterModels'
import { setUserPlanningPreference } from '../../../../redux/management/managementActions'
import { Reducers } from '../../../../redux/reducers'
import { TypedDispatch } from '../../../../redux/store'
import { setTimeMode } from '../../../../redux/weekNavigator/weekNavigatorActions'
import ConceptsListView from '../../containers/ConceptsListView/ConceptsListView'

const ConnectedConceptsListView: FC = (): ReactElement => {
    const dispatch = useDispatch<TypedDispatch>()

    const { selectedDays, timeMode } = useTypedSelector(state => state.calendarReducer)
    const {
        isLoading: isLoadingShiftConcepts,
        isSuccessful,
        isPublishShiftConceptsSuccessful,
        usersWithConceptAvailability: users,
        availabilityPagination: pagination,
    } = useSelector((state: Reducers) => state.shiftConceptsReducer)
    const {
        isLoading: isShiftConceptPlanningLoading,
        isDeleteSuccessful: isDeleteShiftConceptPlanningSuccessful,
        isEditSuccessful: isEditShiftConceptPlanningSuccessful,
        deletedShiftConceptPlanningId,
        shiftConceptPlanning,
    } = useSelector((state: Reducers) => state.shiftConceptPlanningReducer)
    const { payrollPeriods } = useTypedSelector(state => state.pacoPayrollPeriodsReducer)
    const legacyPayrollPeriods = useSelector<Reducers, PayrollPeriodViewModel[]>(state => state.appReducer.payrollPeriods)
    const { specialDays } = useSelector((state: Reducers) => state.specialDaysReducer)
    const { permissions, role: currentUserRole } = useTypedSelector(state => state.authenticatedUserReducer)
    const shiftsLoading = useSelector((state: Reducers) => state.shiftsReducer.loading)
    const absencesLoading = useSelector((state: Reducers) => state.absencesReducer.loading)
    const { isLoading: pacoAbsencesLoading, error: absencesError } = useTypedSelector(state => state.pacoAbsencesReducer)
    const { isLoading: leaveOfAbsencesLoading, error: leaveOfAbsencesError } = useTypedSelector(state => state.pacoLeaveOfAbsencesReducer)
    const managementLoading = useSelector((state: Reducers) => state.managementReducer.loading)
    const { eventLogsByEntity, isLoading: isLogsLoading } = useSelector((state: Reducers) => state.eventLogsReducer)
    const filter = useSelector((state: Reducers) => state.filterReducer.filter)
    const { departmentOptions, userSearch } = useTypedSelector(state => state.globalFiltersReducer)
    const showEmploymentTypes = useTypedSelector(state => state.globalFiltersReducer.showEmploymentTypes)
    const { baseSchedules } = useTypedSelector(state => state.pacoBaseScheduleReducer)
    const isLoading = shiftsLoading || absencesLoading || pacoAbsencesLoading || leaveOfAbsencesLoading || managementLoading || isShiftConceptPlanningLoading || isLoadingShiftConcepts

    const canAddPlannings = checkPermission(permissions, 'add-new-shift-plannings')
    const canViewShiftConcepts = checkPermission(permissions, 'view-all-shift-concepts')

    const startDate = getStartOfWorkDay(selectedDays[0])
    const endDate = getEndOfWorkDay(selectedDays[selectedDays.length - 1])

    const [logsModal, setLogsModal] = useState<boolean>(false)

    useEffect((): void => {
        // Fall back to 'week' if initial time mode is not supported by this view
        if (timeMode !== TimeModeType.week && timeMode !== TimeModeType.custom) {
            dispatch(setTimeMode(TimeModeType.week))
        }

        if (! legacyPayrollPeriods.length) {
            dispatch(getPayrollPeriods())
        }

        if (! payrollPeriods.length) {
            dispatch(getPacoPayrollPeriods())
        }
    }, [])

    useEffect((): void => {
        dispatch(fetchSpecialDays(addDays(startDate, -1), addSeconds(endDate, 1)))
    }, [selectedDays])

    useEffect((): void => {
        if (isSuccessful) {
            dispatch(fetchShiftConceptPlannings(pagination.number))
        }
    }, [isSuccessful])

    useEffect((): void => {
        if (isEditShiftConceptPlanningSuccessful && shiftConceptPlanning) {
            const updatedUsers = updateUsersWithConceptAvailabilityWithShiftPlanning(users, shiftConceptPlanning)

            dispatch(setIsShiftConceptPlanningSuccessful(false))
            dispatch(setUserWithConceptAvailability(updatedUsers))
        }
    }, [isEditShiftConceptPlanningSuccessful])

    useEffect((): void => {
        if (isDeleteShiftConceptPlanningSuccessful && deletedShiftConceptPlanningId) {
            const updatedUsers = removeShiftConceptPlanningFromUsersWithConceptAvailability(users, deletedShiftConceptPlanningId)

            dispatch(setIsShiftConceptPlanningDeleteSuccessful(false))
            dispatch(setUserWithConceptAvailability(updatedUsers))
        }
    }, [isDeleteShiftConceptPlanningSuccessful])

    useEffect((): void => {
        dispatch(setAvailabilityPagination(1))
    }, [departmentOptions, userSearch])

    useEffect((): void => {
        if (leaveOfAbsencesError || absencesError) {
            dispatch(setToast({
                text: leaveOfAbsencesError || absencesError,
                type: ToastType.fail,
                shouldFadeOutAutomatically: false,
            }))

            dispatch(setLeaveOfAbsencesError(''))
            dispatch(setAbsencesError(''))
        }
    }, [leaveOfAbsencesError, absencesError])

    const getUsersData = (): void => {
        dispatch(getUsersListViewData())
    }

    useEffect((): void => {
        getUsersData()
    }, [selectedDays, departmentOptions, userSearch, isPublishShiftConceptsSuccessful])

    const handleSearchBarChange = (search: string): void => {
        dispatch(setUserSearch(search))
    }

    const onSetFilterChange = (newFilter: AppFilter): void => {
        dispatch(setFilter(newFilter, getUsersData))
    }

    const dispatchPlanUserToShiftConcept = (userId: string, shiftConcept: ShiftConceptViewModelOld): void => {
        dispatch(planAvailabilityUserToShiftConcept(userId, shiftConcept))
    }

    const dispatchEditLeaveOfAbsence = async (
        editType: LeaveOfAbsenceEditType,
        data: LeaveOfAbsenceFormData,
        payrollPeriodsWithHours: PayrollPeriodWithHours[],
        weeksWithHours: WeekWithHours[],
        activeLeaveOfAbsence: LeaveOfAbsence,
        leaveType?: LeaveType | PzLeaveType | null,
    ): Promise<void> => {
        switch (editType) {
            case LeaveOfAbsenceEditType.accept:
                await dispatch(approveLeaveOfAbsence(
                    transformLegacyLeaveOfAbsenceToLeaveOfAbsence(activeLeaveOfAbsence),
                    data,
                    activeLeaveOfAbsence.leaveOfAbsenceHours || [],
                    weeksWithHours,
                    leaveType ?? undefined,
                ))
                break
            case LeaveOfAbsenceEditType.delete:
                await dispatch(deleteLeaveOfAbsence(
                    transformLegacyLeaveOfAbsenceToLeaveOfAbsence(activeLeaveOfAbsence),
                ))
                break
            default:
                await dispatch(patchLeaveOfAbsence(
                    transformLegacyLeaveOfAbsenceToLeaveOfAbsence(activeLeaveOfAbsence),
                    data,
                    activeLeaveOfAbsence.leaveOfAbsenceHours || [],
                    weeksWithHours,
                    leaveType ?? undefined,
                ))
                break
        }

        getUsersData()
    }

    const dispatchDeclineLeaveOfAbsence = async (
        declineComment: string,
        editLOAData: LeaveOfAbsenceFormData,
        editLOAPayrollPeriodHours: PayrollPeriodWithHours[],
        editLOAWeekdayHours: WeekWithHours[],
        activeLeaveOfAbsence: LeaveOfAbsence,
    ): Promise<void> => {
        await dispatch(declineLeaveOfAbsence(
            transformLegacyLeaveOfAbsenceToLeaveOfAbsence(activeLeaveOfAbsence),
            declineComment,
        ))

        getUsersData()
    }

    const dispatchEditAbsence = async (
        absence: Absence,
        data: AbsenceFormData,
        weekdays: WeekWithHours[],
    ): Promise<void> => {
        await dispatch(patchAbsence(
            transformLegacyAbsenceToAbsence(absence),
            data,
            absence.absenceHours || [],
            weekdays,
        ))

        getUsersData()
    }

    const dispatchDeleteAbsence = async (absence: Absence): Promise<void> => {
        await dispatch(deleteAbsence(transformLegacyAbsenceToAbsence(absence)))
        getUsersData()
    }

    const dispatchAvailabilityTime = (
        weekday: number,
        fromDate: string,
        toDate: string,
        comment: string,
        userId: string,
        id: string,
    ): void => {
        dispatch(setUserPlanningPreference(
            weekday,
            fromDate,
            toDate,
            comment,
            userId,
            id,
            CONCEPTS_LIST_VIEW,
        ))
    }

    const dispatchGetLeaveOfAbsencesLogs = async (id: string): Promise<void> => {
        await dispatch(getEventLogsByEntityUuid(id))
        setLogsModal(true)
    }

    const dispatchCloseLogsModal = (): void => {
        setLogsModal(false)
    }

    const handlePagination = (page: number): void => {
        dispatch(setAvailabilityPagination(page))
        getUsersData()
    }

    const handleCalendarTypeChange = (): void => {
        dispatch(switchMode())
    }

    const handleSubmitPublishBaseSchedules = (formData: PublishBaseScheduleFormData): void => {
        dispatch(publishBaseSchedule(formData))
    }

    return (
        <>
            <SidebarPage
                sidebarContent={(
                    <NewFilters
                        filter={filter}
                        filterBlocks={[
                            'availability',
                            'availability-sorting',
                            'main-department',
                            'employment-type-badge',
                            'show-published-shifts',
                        ]}
                        permissions={permissions}
                        onChange={onSetFilterChange}
                    />
                )}
            >
                <ConceptsListView
                    isFirstRowSticky
                    isFirstColumnSticky
                    isLoading={isLoading}
                    canAddPlannings={canAddPlannings}
                    canViewShiftConcepts={canViewShiftConcepts}
                    days={selectedDays}
                    baseSchedules={baseSchedules}
                    filter={filter}
                    showEmploymentTypes={showEmploymentTypes}
                    users={users}
                    pagination={pagination}
                    pacoPayrollPeriods={payrollPeriods}
                    payrollPeriods={legacyPayrollPeriods}
                    specialDays={specialDays}
                    startDate={startDate}
                    endDate={endDate}
                    permissions={permissions}
                    currentUserRole={currentUserRole}
                    onSearchBarChange={handleSearchBarChange}
                    onPagination={handlePagination}
                    onCalendarTypeChange={handleCalendarTypeChange}
                    onShowLeaveOfAbsencesLogsClick={dispatchGetLeaveOfAbsencesLogs}
                    onSubmitPublishBaseSchedules={handleSubmitPublishBaseSchedules}
                    dispatchPlanUserToShiftConcept={dispatchPlanUserToShiftConcept}
                    dispatchEditLeaveOfAbsence={dispatchEditLeaveOfAbsence}
                    dispatchDeclineLeaveOfAbsence={dispatchDeclineLeaveOfAbsence}
                    dispatchEditAbsence={dispatchEditAbsence}
                    dispatchDeleteAbsence={dispatchDeleteAbsence}
                    dispatchAvailabilityTime={dispatchAvailabilityTime}
                />
            </SidebarPage>
            <PacoLogsModal
                isLoading={isLogsLoading}
                isOpen={logsModal && ! isLogsLoading}
                title={translate('logs.leaveOfAbsenceLog')}
                emptyMessage={translate('logs.emptyLeaveOfAbsenceLog')}
                logs={eventLogsByEntity}
                onClose={dispatchCloseLogsModal}
            />
        </>
    )
}

export default ConnectedConceptsListView
